/**
 * Phase I-B.2C — Display helpers for Project surfaces (cards + detail).
 *
 * `formatINRDisplay` — Indian price format (₹32 L / ₹1.20 Cr / ₹54,000).
 * `normalisePossession` — turns "2026-08", "Aug 2026", "Currently",
 *   "Ready" into a human-friendly chip label ("Q3 2026", "Ready", etc.).
 *
 * Pure functions; safe for SSR and any React surface.
 */

const PRICE_UNIT_MAP = { inr: 1, lakh: 100000, crore: 10000000 };

export function toCanonicalINR(value, unit) {
  if (value === "" || value === null || value === undefined) return null;
  const n = Number(value);
  if (!Number.isFinite(n)) return null;
  const mult = PRICE_UNIT_MAP[(unit || "").toLowerCase()] || 0;
  if (!mult) return null;
  return Math.round(n * mult);
}

/** Indian-style price formatting. ₹54,000 / ₹32 L / ₹1.20 Cr. */
export function formatINRDisplay(inr) {
  if (inr === null || inr === undefined || inr === "") return "—";
  const n = Number(inr);
  if (!Number.isFinite(n) || n < 0) return "—";
  if (n >= 10000000) {
    const v = n / 10000000;
    return `₹${v % 1 === 0 ? v.toFixed(0) : v.toFixed(2)} Cr`;
  }
  if (n >= 100000) {
    const v = n / 100000;
    return `₹${v % 1 === 0 ? v.toFixed(0) : v.toFixed(1)} L`;
  }
  return `₹${n.toLocaleString("en-IN")}`;
}

/**
 * Legacy heuristic: when reading `projects.units[].price_min` (a long-running
 * field with mixed unit semantics — some rows store lakhs as small integers
 * like 20, 50, others store full INR like 5000000), normalise small positive
 * values (< 1 L) as lakhs before formatting. Real INR values (≥ ₹1 L)
 * pass through unchanged.
 *
 * Returns the canonical INR amount, or null when the input is empty / non-numeric.
 *
 * Use ONLY for legacy unit fields (NOT for canonical inventory). Canonical
 * configurations carry `canonical_price_min_inr` (no heuristic needed).
 */
export function normaliseLegacyUnitPrice(value) {
  if (value === null || value === undefined || value === "") return null;
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return null;
  if (n < 100000) return Math.round(n * 100000); // assume lakhs
  return Math.round(n);
}

/**
 * Indian price RANGE formatting. Drops the redundant suffix when both
 * ends share the same magnitude (₹45 – 55 L instead of ₹45 L – ₹55 L).
 */
export function formatINRRange(min, max) {
  if ((min === null || min === undefined || min === "") && (max === null || max === undefined || max === "")) return "—";
  if (min === null || min === undefined || min === "") return formatINRDisplay(max);
  if (max === null || max === undefined || max === "" || Number(min) === Number(max)) return formatINRDisplay(min);
  const a = Number(min), b = Number(max);
  // Same magnitude → compress
  if (a >= 10000000 && b >= 10000000) {
    return `₹${(a / 10000000).toFixed(a % 10000000 === 0 ? 0 : 2)} – ${(b / 10000000).toFixed(b % 10000000 === 0 ? 0 : 2)} Cr`;
  }
  if (a >= 100000 && b >= 100000 && a < 10000000 && b < 10000000) {
    return `₹${(a / 100000).toFixed(a % 100000 === 0 ? 0 : 1)} – ${(b / 100000).toFixed(b % 100000 === 0 ? 0 : 1)} L`;
  }
  return `${formatINRDisplay(a)} – ${formatINRDisplay(b)}`;
}

const MONTH_NAMES = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const MONTH_LOOKUP = {};
MONTH_NAMES.forEach((m, i) => { MONTH_LOOKUP[m.toLowerCase()] = i + 1; });
[
  ["january", 1], ["february", 2], ["march", 3], ["april", 4], ["may", 5], ["june", 6],
  ["july", 7], ["august", 8], ["september", 9], ["october", 10], ["november", 11], ["december", 12],
].forEach(([name, idx]) => { MONTH_LOOKUP[name] = idx; });

/**
 * Normalise free-text possession into a stable label.
 * Returns { label, ready, q?, year? }. Examples:
 *   "" / "currently" / "ready" → { label: "Ready", ready: true }
 *   "2026-08" / "Aug 2026" → { label: "Q3 2026", year: 2026, q: 3 }
 *   junk → { label: raw, ready: false }
 */
export function normalisePossession(raw) {
  const s = String(raw || "").trim();
  if (!s) return { label: "TBA", ready: false };
  const low = s.toLowerCase();
  if (["currently", "current", "ready", "ready_now", "ready_to_move", "rtm", "completed", "delivered"].includes(low)) {
    return { label: "Ready", ready: true };
  }
  // 2026-08 or 2026-8 — accept; month > 12 falls back to year-only ("2027")
  let m = s.match(/^(\d{4})-(\d{1,2})$/);
  if (m) {
    const year = Number(m[1]), month = Number(m[2]);
    if (year > 1900 && month >= 1 && month <= 12) {
      const q = Math.ceil(month / 3);
      return { label: `Q${q} ${year}`, year, q };
    }
    if (year > 1900) return { label: String(year), year };
  }
  // Aug 2026 / August 2026 / Aug-2026
  m = s.match(/^([a-zA-Z]+)[\s-]+(\d{4})$/);
  if (m) {
    const mm = MONTH_LOOKUP[m[1].toLowerCase()];
    const year = Number(m[2]);
    if (mm) {
      const q = Math.ceil(mm / 3);
      return { label: `Q${q} ${year}`, year, q };
    }
  }
  // 2026 Aug
  m = s.match(/^(\d{4})\s+([a-zA-Z]+)$/);
  if (m) {
    const year = Number(m[1]);
    const mm = MONTH_LOOKUP[m[2].toLowerCase()];
    if (mm) {
      const q = Math.ceil(mm / 3);
      return { label: `Q${q} ${year}`, year, q };
    }
  }
  // Q3 2026 (already normalised) — accept as-is
  m = s.match(/^[qQ]([1-4])[\s-]+(\d{4})$/);
  if (m) return { label: `Q${m[1]} ${m[2]}`, year: Number(m[2]), q: Number(m[1]) };
  // Bare 4-digit year
  m = s.match(/^(\d{4})$/);
  if (m) return { label: m[1], year: Number(m[1]) };
  return { label: s, ready: false };
}
